import React, { useState } from "react";
import { Button, Input } from "../../atoms";
import { Modal, FormField } from "../../molecules";
import type { CreateStartupData } from "@/types/startup.types";

const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "E-commerce",
  "Education",
  "SaaS",
  "Consumer Products",
  "B2B Services",
  "Media & Entertainment",
  "Real Estate",
  "Food & Beverage",
  "Manufacturing",
  "Other",
];

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "CNY"];

interface CreateStartupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: CreateStartupData) => Promise<void>;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

export const CreateStartupModal: React.FC<CreateStartupModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  isLoading = false,
  title = "Create startup",
  description = "Add a new startup to your account. Financial data is kept separate from your other startups.",
}) => {
  const [formData, setFormData] = useState({
    name: "",
    initialFunding: "",
    industry: "",
    description: "",
    currency: "USD",
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Startup name is required");
      return;
    }

    try {
      const foundedDate = new Date();
      await onCreate({
        name: formData.name.trim(),
        initialFunding: formData.initialFunding ? Number(formData.initialFunding) : 0,
        industry: formData.industry || undefined,
        description: formData.description || undefined,
        currency: formData.currency,
        foundedDate,
        startDate: foundedDate,
      });
      setFormData({
        name: "",
        initialFunding: "",
        industry: "",
        description: "",
        currency: "USD",
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create startup");
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-6">
        <p className="text-sm text-white/65">{description}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error ? (
            <div className="rounded-[14px] border border-warn/40 bg-warn/10 p-3">
              <p className="text-sm text-warn">{error}</p>
            </div>
          ) : null}

          <FormField label="Startup Name *" error="">
            <Input
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="e.g., My Amazing Startup"
              disabled={isLoading}
            />
          </FormField>

          <FormField label="Initial Funding ($)" error="">
            <Input
              type="number"
              step="0.01"
              value={formData.initialFunding}
              onChange={(e) => handleChange("initialFunding", e.target.value)}
              placeholder="100000"
              disabled={isLoading}
            />
          </FormField>

          <FormField label="Currency" error="">
            <select
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              disabled={isLoading}
              className="sh-input"
            >
              {CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Industry" error="">
            <select
              value={formData.industry}
              onChange={(e) => handleChange("industry", e.target.value)}
              disabled={isLoading}
              className="sh-input"
            >
              <option value="">Select Industry (Optional)</option>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Description" error="">
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Brief description of your startup... (Optional)"
              disabled={isLoading}
              className="sh-input min-h-[80px] resize-y"
            />
          </FormField>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? "Creating..." : "Create Startup"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
